import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnFiltersState
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Typography
} from '@mui/material';
import { useEmployeeAssignmentColumns } from './columns';
import { useListEmployees } from '../../../../queries/employee';
import { useStore } from '../../../../hooks/useStore';
import { useState, useMemo, useRef } from 'react';
import { globalFilterFn } from '../../../util/filter';
import { EmployeeOverviewToolBar } from './toolbars/employee-overview';
import Pagination from '../shared/pagination';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { observer } from 'mobx-react-lite';
import { DataTableLoadingBar } from '../shared/loading-bar';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { SortableTableHeader } from '../shared/sortable-header';
import { useActions } from '../../../../hooks/useActions';
import { Loading } from '../../atomic';
import { closeSnackbar, SnackbarKey } from 'notistack';

export const EmployeeBenefitSetAssignmentTable = observer(() => {
  const { companyStore } = useStore();
  const { selectedEmployees, selectedUsers, setSelectedEmployees } = useActions();
  const { t } = useTranslation();

  const { data, isFetching, isLoading } = useListEmployees(companyStore.company?.id, {
    enabled: !!companyStore.company?.id
  });

  const employeesData = useMemo(() => {
    const allEmployees = data?.pages?.flatMap((page) => page) ?? [];

    const selectedEmployeeIds = selectedUsers ?? Object.keys(selectedEmployees ?? {});

    if (selectedEmployeeIds.length === 0) {
      return [];
    }

    return allEmployees.filter((employee) => selectedEmployeeIds.includes(employee.id ?? ''));
  }, [data, selectedEmployees, selectedUsers]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const currentRemovalSnackbarRef = useRef<SnackbarKey | null>(null);

  const handleRemoveEmployee = (employeeId: string) => {
    if (currentRemovalSnackbarRef.current !== null) {
      closeSnackbar(currentRemovalSnackbarRef.current);
    }

    const updatedSelection = { ...selectedEmployees };
    delete updatedSelection[employeeId];
    setSelectedEmployees(updatedSelection);
  };

  const handleRestoreEmployee = (employeeId: string) => {
    const updatedSelection = { ...selectedEmployees };
    updatedSelection[employeeId] = true;
    setSelectedEmployees(updatedSelection);

    currentRemovalSnackbarRef.current = null;
  };

  const columns = useEmployeeAssignmentColumns(
    handleRemoveEmployee,
    handleRestoreEmployee,
    currentRemovalSnackbarRef
  );
  const table = useReactTable({
    data: employeesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id ?? 'MISSING_ROW_ID',
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination,
      globalFilter,
      columnFilters
    },
    initialState: {
      columnVisibility: {
        active_benefit: false,
        budget_exhausted: false,
        entry_date: false
      }
    }
  });

  if (isLoading) {
    return (
      <Paper
        sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Paper>
    );
  }

  return (
    <Paper className="overflow-hidden flex flex-col h-[calc(50vh-20px)]">
      <EmployeeOverviewToolBar table={table} />
      <TableContainer sx={scrollableTableContainerSx()}>
        <Table stickyHeader className="bg-white" size="small">
          <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader
                    key={header.id}
                    header={header}
                    isLoading={isFetching}
                    hasData={employeesData.length > 0}
                  />
                ))}
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                sx={{
                  padding: 0,
                  borderBottom: 'none',
                  backgroundColor: 'transparent'
                }}
                colSpan={columns.length}
              >
                <DataTableLoadingBar isLoading={isFetching} />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedEmployees && Object.keys(selectedEmployees).length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="!border-none">
                  <div className="flex flex-col items-center justify-center">
                    <img
                      src={EmptyTable}
                      alt={t('benefitSet:noEmployeesSelected')}
                      className="w-56 56"
                    />
                    <Typography className={'mt-2 text-midnight font-medium'}>
                      {t('benefitSet:noEmployeesSelected')}
                    </Typography>
                    <Typography className={'mt-2  text-grey-600'}>
                      {t('benefitSet:noEmployeesSelectedHint')}
                    </Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="py-2" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination table={table} />
    </Paper>
  );
});
